import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import type { AllExpensesFilters } from '@/api/expenses';
import type { Category } from '@sharecost/shared';

interface GroupOption {
  id: string;
  name: string;
  color: string;
}

type DatePreset = NonNullable<AllExpensesFilters['datePreset']>;

// Labels come from the shared expenses namespace (same wording as mobile)
const DATE_PRESETS: { value: DatePreset; labelKey: string }[] = [
  { value: 'thisMonth', labelKey: 'filter.thisMonth' },
  { value: 'lastMonth', labelKey: 'filter.lastMonth' },
  { value: 'last3Months', labelKey: 'export.last3Months' },
  { value: 'thisYear', labelKey: 'filter.thisYear' },
  { value: 'all', labelKey: 'filter.allTime' },
];

interface ExpenseFiltersProps {
  filters: AllExpensesFilters;
  onFilterChange: (updates: Partial<AllExpensesFilters>) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
  groups?: GroupOption[];
  categories?: Category[];
}

export function ExpenseFilters({
  filters,
  onFilterChange,
  onClear,
  hasActiveFilters,
  groups,
  categories,
}: ExpenseFiltersProps) {
  const { t } = useTranslation(['expenses']);
  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const [showMore, setShowMore] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync external search changes back to input
  useEffect(() => {
    setSearchInput(filters.search ?? '');
  }, [filters.search]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFilterChange({ search: value || undefined });
    }, 300);
  };

  const toggleGroup = (groupId: string) => {
    const current = filters.groupIds ?? [];
    const next = current.includes(groupId)
      ? current.filter(id => id !== groupId)
      : [...current, groupId];
    onFilterChange({ groupIds: next.length > 0 ? next : undefined });
  };

  const toggleCategory = (categoryId: string) => {
    const current = filters.categoryIds ?? [];
    const next = current.includes(categoryId)
      ? current.filter(id => id !== categoryId)
      : [...current, categoryId];
    onFilterChange({ categoryIds: next.length > 0 ? next : undefined });
  };

  const activeFilterCount = [
    filters.search,
    filters.groupIds?.length,
    filters.categoryIds?.length,
    filters.datePreset && filters.datePreset !== 'all',
    filters.myExpensesOnly,
    filters.minAmount !== undefined,
    filters.maxAmount !== undefined,
  ].filter(Boolean).length;

  return (
    <div className="space-y-3">
      {/* Row 1: Search + My Expenses toggle */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-quaternary" />
          <input
            type="text"
            placeholder={t('web:expenseFilters.searchExpenses')}
            value={searchInput}
            onChange={e => handleSearchChange(e.target.value)}
            className="w-full rounded-lg border border-border-secondary bg-surface-primary py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-quaternary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          {searchInput && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-text-quaternary hover:text-text-secondary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <button
          onClick={() => onFilterChange({ myExpensesOnly: !filters.myExpensesOnly })}
          className={`shrink-0 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
            filters.myExpensesOnly
              ? 'bg-brand-600 text-white'
              : 'border border-border-secondary text-text-secondary hover:bg-surface-hover'
          }`}
        >
          {t('web:expenseFilters.myExpenses')}
        </button>
      </div>

      {/* Row 2: Group chips */}
      {groups && groups.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          {groups.map(g => {
            const isSelected = filters.groupIds?.includes(g.id) ?? false;
            return (
              <button
                key={g.id}
                onClick={() => toggleGroup(g.id)}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  isSelected
                    ? 'bg-gray-900 text-white dark:bg-brand-600 dark:text-white'
                    : 'bg-surface-tertiary text-text-secondary hover:bg-surface-inset'
                }`}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: g.color }}
                />
                {g.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Row 3: Date preset pills */}
      <div className="flex items-center gap-1 rounded-lg bg-surface-tertiary p-1">
        {DATE_PRESETS.map(opt => (
          <button
            key={opt.value}
            onClick={() => onFilterChange({
              datePreset: opt.value,
              startDate: undefined,
              endDate: undefined,
            })}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              (filters.datePreset ?? 'all') === opt.value
                ? 'bg-surface-primary text-text-primary shadow-sm'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
          >
            {t(opt.labelKey)}
          </button>
        ))}
      </div>

      {/* Collapsible more filters */}
      <button
        onClick={() => setShowMore(!showMore)}
        className="flex items-center gap-1 text-xs font-medium text-text-tertiary hover:text-text-secondary"
      >
        {showMore ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {t('web:expenseFilters.moreFilters')}
      </button>

      {showMore && (
        <div className="space-y-3 rounded-lg border border-border-tertiary bg-surface-secondary p-3">
          {/* Category chips */}
          {categories && categories.length > 0 && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text-tertiary">{t('expenses:form.category.label')}</label>
              <div className="flex flex-wrap gap-1.5">
                {categories.map(cat => {
                  const isSelected = filters.categoryIds?.includes(cat.id) ?? false;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                        isSelected
                          ? 'bg-gray-900 text-white dark:bg-brand-600 dark:text-white'
                          : 'bg-surface-tertiary text-text-secondary hover:bg-surface-inset'
                      }`}
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Amount range */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-text-tertiary">{t('web:expenseFilters.amountRangeUsd')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder={t('web:expenseFilters.min')}
                value={filters.minAmount ?? ''}
                onChange={e => onFilterChange({ minAmount: e.target.value ? Number(e.target.value) : undefined })}
                className="w-24 rounded-md border border-border-secondary bg-surface-primary px-2 py-1.5 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <span className="text-text-quaternary">—</span>
              <input
                type="number"
                placeholder={t('web:expenseFilters.max')}
                value={filters.maxAmount ?? ''}
                onChange={e => onFilterChange({ maxAmount: e.target.value ? Number(e.target.value) : undefined })}
                className="w-24 rounded-md border border-border-secondary bg-surface-primary px-2 py-1.5 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Active filter count + Clear all */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-tertiary">{t('web:expenseFilters.filtersActive', { count: activeFilterCount })}</span>
          <button
            onClick={onClear}
            className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
          >
            {t('expenses:filter.clearAll')}
          </button>
        </div>
      )}
    </div>
  );
}
